import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

export interface ProductDrillLevel {
  /** A "product" level is always the last entry of the path; nothing drills below it. */
  kind: "group" | "product";
  id: string;
  name: string;
  externalReference: string | null;
}

export interface ProductDrillContextValue {
  /** The drill path, root-first. The last entry is the visible level. */
  stack: ProductDrillLevel[];
  drillInto: (level: ProductDrillLevel) => void;
  /** Truncates the path to its first `depth` entries; 0 returns to the top level. */
  drillTo: (depth: number) => void;
}

const ProductDrillContext = createContext<ProductDrillContextValue | null>(null);

/** Holds the product drill-down path; resets when the customer changes. */
export function ProductDrillProvider({
  customerId,
  children,
}: {
  customerId: string | undefined;
  children: ReactNode;
}) {
  const [stack, setStack] = useState<ProductDrillLevel[]>([]);

  useEffect(() => {
    setStack([]);
  }, [customerId]);

  const value = useMemo<ProductDrillContextValue>(
    () => ({
      stack,
      drillInto: (level) => setStack((previous) => [...previous, level]),
      drillTo: (depth) => setStack((previous) => previous.slice(0, depth)),
    }),
    [stack],
  );

  return <ProductDrillContext.Provider value={value}>{children}</ProductDrillContext.Provider>;
}

export function useProductDrill(): ProductDrillContextValue {
  const context = useContext(ProductDrillContext);
  if (!context) throw new Error("useProductDrill must be used within a ProductDrillProvider");
  return context;
}
